import {
  Avatar,
  Box,
  Button,  
  Dialog,
  DialogActions,
  DialogContent,  
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useEffect, useState } from "react";
import { AccountBalance, Edit, More, Save, WorkOutline } from "@mui/icons-material";
import MaterialInput from "../../../../../../components/MaterialInput";
import axios from "axios";
import BASEURL from "../../../../../../pages/Utils/Baseurl";
import { getUserDetailsById, updateProfileQuery } from "./Queries";
import Swal from "sweetalert2";
import MaterialSelect from "../../../../../../components/MasterSelect";
import { Country, State } from "country-state-city";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// Fields that can be edited by the user
const EDITABLE_FIELDS = new Set([
  "first_name",
  "last_name",
  "fathers_name",
  "marital_status",
  "address",
  "country",
  "state",
  "city",
  "zip_code",
  "aadhar_number",
  "pan_number",
  "gender",
  "date_of_birth",
  "contact_number",
  "emergency_contact",  
]);

// Mandatory fields – Save Changes is disabled when any of these are empty/invalid
const MANDATORY_FIELDS = [
  "first_name",
  "last_name",
  "marital_status",
  "address",
  "country",
  "state",
  "city",
  "zip_code",
  "gender",
  "date_of_birth",
  "contact_number",
  "aadhar_number",
  "pan_number",
];

// Defined outside Profile so its identity stays stable across renders.
const InfoItem = ({
    label,
    field,
    value,
    isEditing,
    editData,
    handleChange,
    options,
    error,
    helperText,
    type = "text",
    maxLength,
    country = "in",
}) => {
    const canEdit = isEditing && field && EDITABLE_FIELDS.has(field);

    // For selects, find the matching option by value, label, or isoCode
    const currentVal = editData?.[field] ?? "";
    const selectValue = options && currentVal
        ? options.find((o) => o.value === currentVal || o.label === currentVal || o.isoCode === currentVal) || { value: currentVal, label: currentVal }
        : null;

    const formattedDisplayValue = () => {
        if (!value) return "-";
        if (type === "phone") {
            const strVal = String(value);
            return strVal.startsWith("+") ? strVal : `+${strVal}`;
        }
        return value;
    };

    return (
        <Stack spacing={0.5}>
            <Typography
                variant="caption"
                sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                }}
            >
                {label}
            </Typography>

            {canEdit ? (
                options ? (
                    <MaterialSelect
                        fullWidth
                        size="small"
                        value={selectValue}
                        onChange={handleChange(field)}
                        options={options}
                        error={error}
                        menuPlacement="top"
                    />
                ) : type === "phone" ? (
                    <PhoneInput
                        country={country || "in"}
                        value={currentVal ? (String(currentVal).startsWith("+") ? String(currentVal) : `+${currentVal}`) : ""}
                        onChange={(val) => handleChange(field)(val)}
                        countryCodeEditable={false}
                        disableDropdown={false}
                        inputProps={{
                            name: field,
                            required: field === "contact_number",
                            style: {
                                borderRadius: "8px",
                                border: error ? "1px solid #d32f2f" : "0.0625rem solid #d2d6da",
                                height: "40px",
                                paddingLeft: "48px",
                                width: "100%",
                            },
                        }}
                        containerStyle={{
                            borderRadius: "8px",
                            width: "100%",
                        }}
                        buttonStyle={{
                            border: "none",
                            backgroundColor: "transparent",
                            borderRadius: "8px 0 0 8px",
                        }}
                        inputStyle={{
                            borderRadius: "0 8px 8px 0",
                            fontSize: "14px",
                            width: "100%",
                            height: "40px",
                            border: error ? "1px solid #d32f2f" : "0.0625rem solid #d2d6da",
                        }}
                    />
                ) : (
                    <MaterialInput
                        fullWidth
                        size="small"
                        label={label}
                        type={type}
                        value={editData?.[field] ?? ""}
                        onChange={handleChange(field)}
                        error={error}
                        inputProps={{ maxLength: maxLength || 200 }}
                    />
                )
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        color: isEditing ? "text.disabled" : "text.primary",
                    }}
                >
                    {formattedDisplayValue()}
                </Typography>
            )}
            {canEdit && error && helperText && (
                <Typography
                    variant="caption"
                    sx={{ color: "error.main", fontSize: "11px", mt: 0.25 }}
                >
                    {helperText}
                </Typography>
            )}
        </Stack>
    );
};

const Profile = ({    
    profileOpen,
    setProfileOpen,
    sessionDetails,
}) => {
    const [userDetails, setUserDetails] = useState({
        first_name: "",
        last_name: "",
        fathers_name: "",
        marital_status: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zip_code: "",
        aadhar_number: "",
        pan_number: "",
        gender: "",
        date_of_birth: "",
        contact_number: "",
        emergency_contact: "",        
    });
    
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editData, setEditData] = useState({});
    const [validation, setValidation] = useState({});

    const fullName =
        `${userDetails?.first_name || ""} ${
        userDetails?.last_name || ""
        }`.trim() || sessionDetails?.name || "";

    useEffect(() => {
        if (!profileOpen) return;
        fetchProfileDetails();
    }, [profileOpen]);

    const fetchProfileDetails = async () => {
        try {
            const response = await axios.post(
                BASEURL,
                {
                    query: getUserDetailsById,
                    variables: { id: sessionDetails?.id },
                },
                {
                    headers: { "Content-Type": "application/json" },
                }
            );
            
            const userDetail = response.data?.data?.userDetail;
                        
            if (userDetail) {
                setUserDetails({
                    first_name: userDetail.firstName || "",
                    last_name: userDetail.lastName || "",
                    fathers_name: userDetail.fathersName || "",
                    gender: userDetail.gender || "",
                    date_of_birth: userDetail.dateOfBirth || "",
                    marital_status: userDetail.maritalStatus || "",
                    contact_number: userDetail.contactNumber || "",                    
                    emergency_contact: userDetail.emergencyContact || "",
                    address: userDetail.address || "",
                    country: userDetail.country || "",
                    state: userDetail.state || "",
                    city: userDetail.city || "",
                    zip_code: userDetail.zipCode || "",
                    aadhar_number: userDetail.aadharNumber || "",
                    pan_number: userDetail.panNumber || "",
                    empid: userDetail?.empid || "",
                    officialMail: userDetail?.officialMail || "",
                    email: userDetail.email || "",
                    department: userDetail.department || "",
                    designation: userDetail?.designation || "",
                    dateOfJoining: userDetail?.dateOfJoining || userDetail?.dateJoined || "",
                    esicNumber: userDetail?.esicNumber || "",
                    uanNumber: userDetail?.uanNumber || "",
                    bankName: userDetail?.bankDetails?.bankName || "",
                    branchName: userDetail?.bankDetails?.branchName || "",
                    accountNumber: userDetail?.bankDetails?.accountNumber || "",
                    ifscNumber: userDetail?.bankDetails?.ifscNumber || "",
                    cifNumber: userDetail?.bankDetails?.cifNumber || "",
                });
            }
        } catch (error) {
            console.error("Error Fetching Employee Details", error);
        }
    };

    const formatDate = (date) => {
        if (!date) return "-";
        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return date;
        return parsedDate.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    // Validates a single field; returns error string or "" for valid
    const validateField = (field, value) => {
        switch (field) {
            case "first_name":
            case "last_name":
                {
                if (!value?.trim()) return "Required";
                if (!/^[a-zA-Z\s]{1,30}$/.test(value.trim()))
                    return "Only alphabets allowed (max 30 chars)";
                return "";
            }
            case "fathers_name": {
                if (!/^[a-zA-Z\s]{0,30}$/.test(value.trim()))
                    return "Only alphabets allowed (max 30 chars)";
                return "";
            }
            case "contact_number": {
                if (!value) return "Required";
                const digits = String(value).replace(/\D/g, "");
                if (digits.length <= 4) return "Required";
                if (digits.length < 8 || digits.length > 15) return "Enter a valid phone number";
                return "";
            }
            case "emergency_contact": {
                if (!value) return "";
                const digits = String(value).replace(/\D/g, "");
                if (digits.length <= 4) return "";
                if (digits.length < 8 || digits.length > 15) return "Enter a valid phone number";
                return "";
            }
            case "zip_code": {
                if (!value) return "Required";
                if (!/^[0-9]{5,6}$/.test(value)) return "Enter a valid 5 or 6 digit ZIP code";
                return "";
            }
            case "aadhar_number": {
                if (!value) return "Required";
                if (!/^[0-9]{12}$/.test(value)) return "Enter a valid 12-digit Aadhar number";
                return "";
            }
            case "pan_number": {
                if (!value) return "Required";
                if (!/^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/.test(value))
                    return "Enter a valid PAN (e.g. ABCDE1234F)";
                return "";
            }
            case "address": {
                if (!value?.trim()) return "Required";
                if (value.trim().length > 200) return "Max 200 characters";
                return "";
            }
            case "country":
            case "state":
            case "city": {
                if (!value?.trim()) return "Required";
                if (value.trim().length > 50) return "Max 50 characters";
                return "";
            }
            case "date_of_birth": {
                if (!value) {
                    return "Required";
                }

                const dob = new Date(value);
                const today = new Date();

                let age = today.getFullYear() - dob.getFullYear();

                const monthDifference =
                    today.getMonth() - dob.getMonth();

                if (
                    monthDifference < 0 ||
                    (monthDifference === 0 &&
                        today.getDate() < dob.getDate())
                ) {
                    age--;
                }

                return age >= 18
                    ? ""
                    : "Employee must be at least 18 years old";
            }
            case "gender":
            case "marital_status":
            default:
                return "";
        }
    };

    // handleChange: MaterialSelect fires with {value, label} option object;
    // MaterialInput fires a DOM event. We always store plain strings.
    const handleChange = (field) => (event) => {
        const rawValue = event?.target !== undefined ? event.target.value : event;
        const value = rawValue && typeof rawValue === "object" && "value" in rawValue
            ? rawValue.value
            : rawValue;

        setEditData((prev) => {
            const next = {
                ...prev,
                [field]: value,
            };
            if (field === "country" && prev.country !== value) {
                next.state = "";
            }
            return next;
        });

        setValidation((prev) => ({
            ...prev,
            [field]: validateField(field, value),
            ...(field === "country" ? { state: validateField("state", "") } : {}),
        }));
    };

    const handleEdit = () => {
        const seed = {};
        EDITABLE_FIELDS.forEach((f) => { seed[f] = userDetails[f] || ""; });
        setEditData(seed);
        // Pre-validate so errors show on blank mandatory fields immediately
        const initVal = {};
        EDITABLE_FIELDS.forEach((f) => { initVal[f] = validateField(f, seed[f]); });
        setValidation(initVal);
        setIsEditing(true);
    };

    // Country options
    const countryOptions = Country.getAllCountries().map((c) => ({
        value: c.name,
        label: c.name,
        isoCode: c.isoCode,
    }));

    // Find selected country isoCode from current editData or initial userDetails
    const selectedCountryName = editData?.country || userDetails?.country || "";
    const selectedCountryObj = Country.getAllCountries().find(
        (c) => c.name === selectedCountryName || c.isoCode === selectedCountryName
    );

    // State options dynamically populated based on selected Country
    const stateOptions = selectedCountryObj
        ? State.getStatesOfCountry(selectedCountryObj.isoCode).map((s) => ({
            value: s.name,
            label: s.name,
            isoCode: s.isoCode,
        }))
        : [];

    // Save is disabled when saving, any mandatory field is missing, or any field has a validation error
    const isSaveDisabled = isSaving ||
        MANDATORY_FIELDS.some((f) => {
            const val = editData[f] ?? "";
            if (!val) return true;
            return Boolean(validateField(f, val));
        }) ||
        Object.values(validation).some(Boolean);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const cleanPhone = (val) => {
                if (!val) return "";
                const digits = String(val).replace(/\D/g, "");
                return digits.length <= 4 ? "" : digits;
            };

            const response = await axios.post(
                BASEURL,
                {
                    query: updateProfileQuery,
                    variables: {
                        input: {
                            userId: sessionDetails?.id,
                            firstName: editData.first_name,
                            lastName: editData.last_name,
                            fathersName: editData.fathers_name,
                            maritalStatus: editData.marital_status,
                            address: editData.address,
                            country: editData.country,
                            state: editData.state,
                            city: editData.city,
                            zipCode: editData.zip_code,
                            aadharNumber: editData.aadhar_number,
                            panNumber: editData.pan_number?.toUpperCase(),
                            gender: editData.gender,
                            dateOfBirth: editData.date_of_birth,
                            contactNumber: cleanPhone(editData.contact_number),
                            emergencyContact: cleanPhone(editData.emergency_contact),                        
                        },
                    },
                },
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const result = response.data?.data?.editProfile;
            if (result?.success) {
                Swal.fire({
                    position: "center",
                    icon: "success",
                    title: result?.message || "Profile Updated Successfully!",
                    showConfirmButton: false,
                    timer: 2500,
                });
                setUserDetails((prev) => ({
                    ...prev,
                    ...editData,
                    contact_number: cleanPhone(editData.contact_number),
                    emergency_contact: cleanPhone(editData.emergency_contact),
                    pan_number: editData.pan_number?.toUpperCase() || prev.pan_number,
                }));
                setIsEditing(false);
                setValidation({});
            } else {
                console.error("Save failed:", result?.message);
                Swal.fire({
                    position: "center",
                    icon: "error",
                    title: result?.message || "Profile Update Failed",
                    showConfirmButton: false,
                    timer: 2500,
                });
            }
        } catch (error) {
            console.error("Error saving profile:", error);
            Swal.fire({
                position: "center",
                icon: "error",
                title: error?.response?.data?.errors[0]?.message || "Profile Update Failed",
                showConfirmButton: false,
                timer: 2500,
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Shorthand: produces common InfoItem props for editable fields
    const ip = (field) => ({
        field,
        isEditing,
        editData,
        handleChange,
        country: selectedCountryObj?.isoCode?.toLowerCase() || "in",
        error: Boolean(validation[field]),
        helperText: validation[field] || "",
    });

 

    const SectionHeader = ({ icon, title }) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Box
                sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f0f1fa",
                    color: "#3f4277",
                }}
            >
                {icon}
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#2f3152" }}>
                {title}
            </Typography>
        </Stack>
    );

    return (
        <Dialog
            open={profileOpen}
            onClose={() => { setProfileOpen(false); setIsEditing(false); setValidation({}); }}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { overflow: "hidden", maxHeight: "90vh" },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: "linear-gradient(135deg, #3f4277 0%, #3f4277 60%, #a1bfa3 100%)",
                    px: 1.5,
                    py: 1.5,
                    color: "#fff",
                    position: "relative",
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                            src={userDetails?.picture || ""}
                            alt={fullName}
                            sx={{
                                width: 45,
                                height: 45,
                                fontSize: "20px",
                                fontWeight: 700,
                                backgroundColor: "#fff",
                                color: "#3f4277",
                            }}
                        >
                            {fullName ? fullName.charAt(0).toUpperCase() : ""}
                        </Avatar>

                        <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: "#fff" }}>
                                {fullName || "-"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                                {userDetails?.empid}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                        {!isEditing && (
                            <IconButton
                                onClick={handleEdit}
                                sx={{
                                    color: "#fff",
                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                                }}
                            >
                                <Edit />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={() => { setProfileOpen(false); setIsEditing(false); setValidation({}); }}
                            sx={{
                                color: "#fff",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </Box>

            <DialogContent sx={{ p: 3, backgroundColor: "#fafafa" }}>
                {/* Personal Information */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 1, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<PersonOutlineIcon fontSize="small" />} title="Personal Information" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="First Name" value={userDetails?.first_name} maxLength={50} {...ip("first_name")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Last Name" value={userDetails?.last_name} maxLength={50} {...ip("last_name")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Father's Name" value={userDetails?.fathers_name} maxLength={50} {...ip("fathers_name")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem
                                label="Gender"
                                value={userDetails?.gender}
                                options={[
                                    { value: "Male", label: "Male" },
                                    { value: "Female", label: "Female" },
                                    { value: "Other", label: "Other" },
                                ]}
                                {...ip("gender")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Date of Birth" type="date" value={formatDate(userDetails?.date_of_birth)} {...ip("date_of_birth")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem
                                label="Marital Status"
                                value={userDetails?.marital_status}
                                options={[
                                    { value: "Single", label: "Single" },
                                    { value: "Married", label: "Married" },
                                    { value: "Divorced", label: "Divorced" },
                                    { value: "Separated", label: "Separated" },
                                    { value: "Widowed", label: "Widowed" },
                                ]}
                                {...ip("marital_status")}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Contact Information */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 1, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<ContactPhoneOutlinedIcon fontSize="small" />} title="Contact Information" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Personal Email" value={userDetails?.officialMail} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Contact Number" type="phone" value={userDetails?.contact_number} {...ip("contact_number")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Emergency Contact" type="phone" value={userDetails?.emergency_contact} {...ip("emergency_contact")} />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Address */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 1, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<LocationOnOutlinedIcon fontSize="small" />} title="Address" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12}>
                            <InfoItem label="Address" value={userDetails?.address} maxLength={200} {...ip("address")} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <InfoItem label="Country" value={userDetails?.country} maxLength={50} {...ip("country")} options={countryOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <InfoItem label="State" value={userDetails?.state} maxLength={50} {...ip("state")} options={stateOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <InfoItem label="City" value={userDetails?.city} maxLength={50} {...ip("city")} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <InfoItem label="ZIP Code" value={userDetails?.zip_code} maxLength={6} {...ip("zip_code")} />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Employment Information – all read-only */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 1, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<WorkOutline fontSize="small" />} title="Employment Information" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Email" value={userDetails?.email} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Department" value={userDetails?.department} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Designation" value={userDetails?.designation} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Date of Joining" value={formatDate(userDetails?.dateOfJoining)} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Identification */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 1, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<BadgeOutlinedIcon fontSize="small" />} title="Identification" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Aadhar Number" value={userDetails?.aadhar_number} maxLength={12} {...ip("aadhar_number")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="PAN Number" value={userDetails?.pan_number} maxLength={10} {...ip("pan_number")} />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Bank Details – all read-only */}
                <Paper elevation={0} sx={{ p: 2.5, mb: 1, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<AccountBalance fontSize="small" />} title="Bank Details" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Bank Name" value={userDetails?.bankName} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Account Number" value={userDetails?.accountNumber} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="Branch Name" value={userDetails?.branchName} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="IFSC Number" value={userDetails?.ifscNumber} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="CIF Number" value={userDetails?.cifNumber} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Other Details – all read-only */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #e8e8ee", backgroundColor: "#fff" }}>
                    <SectionHeader icon={<More fontSize="small" />} title="Other Details" />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="UAN" value={userDetails?.uanNumber} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="ESIC Number" value={userDetails?.esicNumber} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoItem label="CIF Number" value={userDetails?.cifNumber} isEditing={isEditing} editData={editData} handleChange={handleChange} />
                        </Grid>
                    </Grid>
                </Paper>
            </DialogContent>

            {isEditing && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        disabled={isSaving}
                        onClick={() => {
                            setEditData({});
                            setIsEditing(false);
                            setValidation({});
                        }}
                        sx={{
                            padding: "10px 18px",
                            backgroundColor: "#dc3545",
                            color: "#fff",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                            transition: "background-color 0.3s ease",
                            "&:hover": { backgroundColor: "#b02a37" },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        disabled={isSaving || isSaveDisabled}
                        onClick={handleSave}
                        sx={{
                            padding: "10px 18px",
                            backgroundColor: "#3f4277",
                            color: "#fff",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                            transition: "background-color 0.3s ease",
                            "&:hover": { backgroundColor: "#34365f" },
                        }}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default Profile;
